import { useCallback } from "react";
import { Platform } from "react-native";

import { setCookiesForViewJob } from "../../../../../../shared/native-modules/core/setCookies";
import { useTareData } from "../../../../../../shared/native-modules/tare/TareDataContext";
import { CookieKey } from "../../../../../../shared/native-modules/types/RNCoreNativeModule";
import { useIsBottomSheet } from "../../../../../../shared/util/ScreenType";
import { httpGetJson } from "../../../../../native-module/httpGetJson";
import { updateApplyStartCookie } from "../../../../../native-module/updateApplyStartCookie";
import { useDwellTiming } from "../../../../context/DwellTiming";
import {
  useDomainUrl,
  useViewJobDataContext,
  useViewjobOneGraphModel,
} from "../../../../context/ViewJobDataContext";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";
import { resolveRawClickSurface, triggerRawClickLogging } from "./jobClickTracking";

/**
 * Sets the desktop click-tracking cookie (RCLK) for the apply-start request (web only).
 */
async function setRclkClickCookie(
  clickCookieName: string | null | undefined,
  clickCookieValue: string | null | undefined,
): Promise<void> {
  if (clickCookieName !== CookieKey.desktopClickTrackingLog || !clickCookieValue) {
    return;
  }
  await setCookiesForViewJob([
    { name: CookieKey.desktopClickTrackingLog, value: clickCookieValue },
  ]);
}

export interface CaptureThirdPartyApplyIntentArgs {
  /** Defaults to the OneGraph model's job key / mobvjtk / sponsored flag. */
  jobKey?: string;
  mobtk?: string;
  sponsored?: boolean;
  /**
   * `false` for a real user tap (manual third-party Apply) → `iststd=1`.
   * `true` for a programmatic/synthetic invocation (ZIA Auto apply capture,
   * GFJ auto-click) → `iststd=0`. Maps to server `isButtonClickTrusted`.
   */
  isSyntheticClick: boolean;
  /** Web-only RCLK cookie inputs; omitted on the native ZIA path. */
  clickCookieName?: string | null;
  clickCookieValue?: string | null;
  /** Whether this was the auto-opened first job (desktop two-pane); manual path only. */
  isFirstAutoLoad?: boolean;
}

type LogApplyStartArgs = Pick<
  CaptureThirdPartyApplyIntentArgs,
  "mobtk" | "sponsored" | "isSyntheticClick" | "clickCookieName" | "clickCookieValue"
>;

/**
 * Shared third-party apply-intent capture (RN-8455). Encapsulates the
 * non-navigation portion of the third-party Apply flow so both the manual
 * `ApplyButton.performApply` path and the ZIA `ViewJobMainContent.handleAutoApply`
 * path record the same apply-start tracking:
 *
 * - `triggerRawClickLogging` (`ia: false`, `isApplyStartClick: true`)
 * - `markApplyStart()` dwell timing
 * - `GET /m/rpc/log/apply-start` (`iststd` = 0 synthetic / 1 trusted)
 * - `updateApplyStartCookie(jobKey, "v", domain)` (RJAS)
 *
 * It never navigates, opens the external Apply modal, or emits external-apply /
 * PostApplyAd / employer-outreach side effects — callers own those.
 */
export function useThirdPartyApplyCapture(): {
  captureThirdPartyApplyIntent: (args: CaptureThirdPartyApplyIntentArgs) => void;
  logThirdPartyApplyStart: (args: LogApplyStartArgs) => void;
} {
  const domain = useDomainUrl();
  const ogModel = useViewjobOneGraphModel();
  const { initialData, from, proctorGroups } = useViewJobDataContext();
  const rnViewJobDisplay = useRnViewJobDisplay();
  const isBottomSheet = useIsBottomSheet();
  const tareData = useTareData();
  const { markApplyStart } = useDwellTiming();

  const isLoggedIn = tareData?.accountStatus?.isLoggedIn ?? false;
  const accountId = tareData?.accountStatus?.accountId;
  const ctk = ogModel?.ctk;
  const dgToken = initialData?.dgToken;
  const clickCookieName = ogModel?.thirdPartyApply?.clickCookieName;
  const clickCookieValue = ogModel?.thirdPartyApply?.clickCookieValue;
  const defaultJobKey = ogModel?.job.jobKey;
  const defaultMobtk = ogModel?.mobvjtk;
  const defaultSponsored = ogModel?.job.isSponsored ?? !!initialData?.sponsored;

  const logThirdPartyApplyStart = useCallback(
    ({
      mobtk = defaultMobtk,
      sponsored = defaultSponsored,
      isSyntheticClick,
      clickCookieName: overrideCookieName,
      clickCookieValue: overrideCookieValue,
    }: LogApplyStartArgs) => {
      if (!mobtk) {
        return;
      }
      const baseUrl = Platform.OS === "web" ? window.location.origin : domain;
      if (!baseUrl) {
        return;
      }
      const cookieName = overrideCookieName ?? clickCookieName;
      const cookieValue = overrideCookieValue ?? clickCookieValue;
      const thirdPartyApplyLogUrl = new URL("/m/rpc/log/apply-start", baseUrl);
      thirdPartyApplyLogUrl.searchParams.set("showPassportModel", isLoggedIn ? "0" : "1");
      thirdPartyApplyLogUrl.searchParams.set("tk", mobtk);
      thirdPartyApplyLogUrl.searchParams.set("surface", rnViewJobDisplay);
      thirdPartyApplyLogUrl.searchParams.set("spn", sponsored ? "1" : "0");
      // A real user tap is a trusted click (iststd=1); a synthetic/programmatic
      // invocation (ZIA capture, GFJ auto-click) reports iststd=0. The server maps
      // this to isButtonClickTrusted (matches jobseeker-frontend).
      thirdPartyApplyLogUrl.searchParams.set("iststd", isSyntheticClick ? "0" : "1");
      if (Platform.OS === "web") {
        if (isSyntheticClick) {
          // The auto-click navigates the current tab immediately; fire with keepalive
          // so the request survives the navigation. Best-effort — swallow rejections.
          void fetch(thirdPartyApplyLogUrl.toString(), { keepalive: true }).catch(() => {});
        } else {
          // Set the RCLK click-tracking cookie before firing so it is attached to the
          // same-origin apply-start fetch. The server-provided value always wins.
          // Best-effort — swallow rejections (setCookiesForViewJob can reject; .finally
          // re-throws it) so cookie failures don't surface as unhandled rejections.
          void setRclkClickCookie(cookieName, cookieValue)
            .finally(() => {
              void fetch(thirdPartyApplyLogUrl.toString()).catch(() => {});
            })
            .catch(() => {});
        }
      } else {
        // On native, route through the native bridge so authenticated cookies/headers
        // are attached. The server marks the job as MAYBE_APPLIED based on this call;
        // the response body is irrelevant so we swallow any error (e.g. non-JSON body).
        void httpGetJson(thirdPartyApplyLogUrl.toString()).catch(() => {});
      }
    },
    [
      domain,
      isLoggedIn,
      rnViewJobDisplay,
      clickCookieName,
      clickCookieValue,
      defaultMobtk,
      defaultSponsored,
    ],
  );

  const captureThirdPartyApplyIntent = useCallback(
    ({
      jobKey = defaultJobKey,
      mobtk = defaultMobtk,
      sponsored = defaultSponsored,
      isSyntheticClick,
      clickCookieName: overrideCookieName,
      clickCookieValue: overrideCookieValue,
      isFirstAutoLoad = false,
    }: CaptureThirdPartyApplyIntentArgs) => {
      // apply-start rawclick (fires on every third-party apply intent, web + native)
      triggerRawClickLogging({ tk: mobtk, jobKey }, isLoggedIn, {
        surface: resolveRawClickSurface(from, rnViewJobDisplay, isBottomSheet),
        ia: false,
        isApplyStartClick: true,
        isFirstAutoLoad,
        dgToken,
        viewJobDisplay: rnViewJobDisplay,
        ctk,
        accountId,
        grps: proctorGroups,
      });
      markApplyStart();
      logThirdPartyApplyStart({
        mobtk,
        sponsored,
        isSyntheticClick,
        clickCookieName: overrideCookieName,
        clickCookieValue: overrideCookieValue,
      });
      if (jobKey) {
        // Best-effort — swallow rejections so a failed cookie write doesn't surface
        // as an unhandled rejection during the apply flow.
        void updateApplyStartCookie(jobKey, "v", domain).catch(() => {});
      }
    },
    [
      isLoggedIn,
      from,
      rnViewJobDisplay,
      isBottomSheet,
      dgToken,
      ctk,
      accountId,
      proctorGroups,
      markApplyStart,
      logThirdPartyApplyStart,
      domain,
      defaultJobKey,
      defaultMobtk,
      defaultSponsored,
    ],
  );

  return { captureThirdPartyApplyIntent, logThirdPartyApplyStart };
}
